import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { getIcon, LEVEL_COLORS, LEVEL_LABELS } from "@/lib/agents";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  ArrowLeft, Send, Terminal, Cpu, Shield, ScrollText, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

export default function AgentConsole() {
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reasoning, setReasoning] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [autonomy, setAutonomy] = useState(1);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/agents/${agentId}`).then((r) => {
      setAgent(r.data);
      setAutonomy(r.data.autonomy_level);
    });
  }, [agentId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, timestamp: new Date().toISOString() }]);
    setSending(true);
    try {
      const { data } = await api.post(`/agents/${agentId}/chat`, {
        message: msg,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setReasoning(data.reasoning || []);
      setMessages((m) => [...m, { role: "agent", content: data.reply, timestamp: data.timestamp }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo al enviar");
    } finally {
      setSending(false);
    }
  };

  const updateAutonomy = async (val) => {
    setAutonomy(val);
    try {
      await api.patch(`/agents/${agentId}/autonomy`, { autonomy_level: val });
      toast.success(`Autonomía actualizada a nivel ${val}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bloqueado");
      // revert
      setAutonomy(agent.autonomy_level);
    }
  };

  const requestApproval = async () => {
    try {
      await api.post("/approvals", {
        agent_id: agentId,
        action_type: "manual.simulate",
        title: `Acción propuesta por ${agent.name}`,
        description: "Simulación de acción que requiere aprobación humana",
        payload: { last_input: messages[messages.length - 1]?.content?.slice(0, 200) || "" },
        risk_level: autonomy >= 3 ? "high" : "medium",
      });
      toast.success("Aprobación enviada a la bandeja");
    } catch (err) {
      toast.error("No se pudo crear aprobación");
    }
  };

  if (!agent) {
    return <div className="p-8 text-[var(--text-dim)]" data-testid="console-loading">Cargando consola…</div>;
  }

  const Icon = getIcon(agent.icon);

  return (
    <div className="min-h-screen grid grid-cols-[1fr_320px]" data-testid="console-page">
      {/* Left: Chat */}
      <div className="flex flex-col border-r border-[var(--border)]">
        {/* Header */}
        <div className="border-b border-[var(--border)] bg-[var(--surface)] px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <Link to="/agents" className="text-[var(--text-dim)] hover:text-white" data-testid="btn-back">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div
              className="w-10 h-10 border flex items-center justify-center"
              style={{ borderColor: agent.color, color: agent.color }}
            >
              <Icon className="w-5 h-5" strokeWidth={2} />
            </div>
            <div>
              <div className="label-mono">{agent.department}</div>
              <div className="font-display font-bold text-lg tracking-tight">{agent.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="rounded-none bg-[var(--surface-2)] border border-[var(--border)] text-white hover:bg-[var(--surface-2)] font-mono text-[10px]">
              <Cpu className="w-3 h-3 mr-1" /> {agent.provider}/{agent.model}
            </Badge>
            <Badge
              className="rounded-none font-mono text-[10px]"
              style={{ background: LEVEL_COLORS[autonomy], color: "#0B0E14" }}
            >
              L{autonomy} · {LEVEL_LABELS[autonomy]}
            </Badge>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-auto p-6 space-y-4" data-testid="messages">
          {messages.length === 0 && (
            <div className="max-w-xl mx-auto text-center py-12">
              <div
                className="w-16 h-16 border mx-auto mb-4 flex items-center justify-center"
                style={{ borderColor: agent.color }}
              >
                <Icon className="w-8 h-8" style={{ color: agent.color }} />
              </div>
              <div className="font-display font-bold text-2xl mb-2">{agent.name}</div>
              <p className="text-sm text-[var(--text-dim)] leading-relaxed">
                {agent.mission}
              </p>
              <div className="mt-6 grid grid-cols-1 gap-2 max-w-md mx-auto">
                {agent.capabilities?.slice(0, 4).map((c) => (
                  <button
                    key={c}
                    onClick={() => setInput(`${c}. Dame un plan detallado y accionable.`)}
                    className="text-left text-xs border border-[var(--border)] px-3 py-2 hover:border-[var(--accent)] font-mono text-[var(--text-dim)] hover:text-white"
                    data-testid={`suggest-${c.slice(0, 20)}`}
                  >
                    → {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-2xl border px-4 py-3 whitespace-pre-wrap ${
                  m.role === "user"
                    ? "border-[var(--accent)]/40 bg-[var(--accent)]/5"
                    : "border-[var(--border)] bg-[var(--surface)]"
                }`}
                data-testid={`msg-${m.role}-${i}`}
              >
                <div className="label-mono mb-2 flex items-center gap-2">
                  {m.role === "user" ? "TÚ" : agent.code}
                  <span className="text-[var(--text-dim)]">·</span>
                  <span className="text-[var(--text-dim)] font-mono">
                    {new Date(m.timestamp).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="text-sm leading-relaxed text-white">{m.content}</div>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="sending-indicator">
              <div className="border border-[var(--border)] bg-[var(--surface)] px-4 py-3">
                <div className="label-mono mb-2">{agent.code} · procesando…</div>
                <div className="font-mono text-sm text-[var(--accent)] cursor-blink">
                  {`${agent.provider} inferring`}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-[var(--border)] bg-[var(--surface)] p-4">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={`Ordena a ${agent.name}… (Enter para enviar, Shift+Enter para nueva línea)`}
              className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-sm min-h-[70px] resize-none"
              data-testid="chat-input"
            />
            <Button
              onClick={send}
              disabled={sending || !input.trim()}
              className="rounded-none h-[70px] bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold px-6"
              data-testid="chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Right: Command panel */}
      <div className="bg-[var(--surface)] p-5 space-y-6 overflow-auto">
        <div>
          <div className="label-mono mb-3 flex items-center gap-2">
            <Shield className="w-3 h-3" /> AUTONOMÍA
          </div>
          <div
            className="font-display font-black text-3xl mb-3"
            style={{ color: LEVEL_COLORS[autonomy] }}
            data-testid="autonomy-level"
          >
            L{autonomy}
          </div>
          <div className="text-xs text-[var(--text-dim)] mb-4">
            {LEVEL_LABELS[autonomy]}
          </div>
          <Slider
            value={[autonomy]}
            min={0}
            max={5}
            step={1}
            onValueChange={(v) => updateAutonomy(v[0])}
            data-testid="autonomy-slider"
          />
          <div className="flex justify-between font-mono text-[10px] text-[var(--text-dim)] mt-2">
            {[0, 1, 2, 3, 4, 5].map((l) => (
              <span key={l} style={{ color: l === autonomy ? LEVEL_COLORS[l] : undefined }}>L{l}</span>
            ))}
          </div>
        </div>

        {/* Reasoning trace */}
        <div>
          <div className="label-mono mb-3 flex items-center gap-2">
            <Terminal className="w-3 h-3" /> TRACE
          </div>
          <div
            className="border border-[var(--border)] bg-[var(--bg)] p-3 font-mono text-[11px] leading-relaxed min-h-[100px]"
            data-testid="reasoning-trace"
          >
            {reasoning.length === 0 ? (
              <span className="text-[var(--text-dim)]">Esperando primera acción…</span>
            ) : (
              reasoning.map((r, i) => (
                <div key={i} className="text-[var(--good)]">{`> ${r}`}</div>
              ))
            )}
          </div>
        </div>

        {/* Capabilities */}
        {agent.capabilities && (
          <div>
            <div className="label-mono mb-3 flex items-center gap-2">
              <ScrollText className="w-3 h-3" /> CAPACIDADES
            </div>
            <ul className="space-y-2">
              {agent.capabilities.map((c) => (
                <li key={c} className="text-xs text-[var(--text-dim)] flex items-start gap-2">
                  <span className="text-[var(--accent)] font-mono">→</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Approval action */}
        <div className="border-t border-[var(--border)] pt-4">
          <Button
            onClick={requestApproval}
            variant="outline"
            className="w-full rounded-none border-[var(--warn)] text-[var(--warn)] hover:bg-[var(--warn)]/10"
            data-testid="btn-request-approval"
          >
            <AlertTriangle className="w-3 h-3 mr-2" /> Solicitar aprobación humana
          </Button>
          <p className="text-[10px] text-[var(--text-dim)] mt-2 leading-relaxed font-mono">
            Acciones sensibles (L3+) requieren aprobación del humano operador.
          </p>
        </div>
      </div>
    </div>
  );
}
